import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Alert, CircularProgress, TextField, Button, FormControlLabel, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { appLogsAPI } from '../services/api';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const LEVEL_COLOR = { ERROR: 'error', WARNING: 'warning', INFO: 'default', DEBUG: 'default' };

function AppLogs() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState('24');
  const [show, setShow] = useState('problems');
  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [auto, setAuto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await appLogsAPI.list({ hours: Number(hours), show, source, q: search.trim() || undefined, limit: 500 });
      setData(res.data); setError(null);
    } catch (err) { setError(errText(err, 'Could not load the application log')); setData((d) => d || { entries: [] }); }
    setLoading(false);
  }, [hours, show, source, search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!auto) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [auto, load]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Application Logs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The portal's own server log and the web server (nginx) error log, newest first. Tokens, product and instance keys, passwords and email addresses are masked.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField select size="small" label="Period" value={hours} onChange={(e) => setHours(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 150 }}>
          <option value="1">Last hour</option><option value="6">Last 6 hours</option><option value="24">Last 24 hours</option>
          <option value="72">Last 3 days</option><option value="168">Last 7 days</option>
        </TextField>
        <TextField select size="small" label="Show" value={show} onChange={(e) => setShow(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 220 }}>
          <option value="problems">Problems (warnings, errors, failed requests)</option>
          <option value="warnings">Warnings and errors from the app</option>
          <option value="all">Everything</option>
        </TextField>
        <TextField select size="small" label="Source" value={source} onChange={(e) => setSource(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 150 }}>
          <option value="all">All sources</option><option value="app">Portal app</option><option value="nginx">nginx (web server)</option>
        </TextField>
        <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
        <FormControlLabel control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />} label="Refresh every 15 s" />
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {(data?.notes || []).map((n) => <Alert key={n} severity="warning" sx={{ mb: 2 }}>{n}</Alert>)}
      {data === null ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {data.entries.length === 0 ? 'Nothing matches.' : `Showing ${data.entries.length} of ${data.matched} matching lines (${data.scanned} lines read).`}
              {data.truncated && ' The period has more lines than can be read; only the newest were searched.'}
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell sx={{ width: 170 }}>Time</TableCell><TableCell sx={{ width: 100 }}>Level</TableCell><TableCell sx={{ width: 80 }}>Source</TableCell><TableCell>Message</TableCell></TableRow></TableHead>
              <TableBody>
                {data.entries.map((e, i) => (
                  <TableRow key={`${e.time}-${i}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(e.time).toLocaleString()}</TableCell>
                    <TableCell><Chip size="small" label={e.level} color={LEVEL_COLOR[e.level] || 'default'} /></TableCell>
                    <TableCell>{e.source || 'app'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{e.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

export default AppLogs;
